import { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation } from 'react-router-dom-v5-compat';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { InsightQueriesPreviewResults } from 'dashboard/models/insight-queries';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  clearInsightQueriesResults,
  setActiveQueryByToken,
} from 'dashboard/redux/slices/insight-queries';
import { RootState } from 'dashboard/redux/store';
import {
  checkExpandedTableExists,
  createExporterInsightQuery,
  createInsightQuery,
  expandData,
  getInsightQueriesPreview,
  updateInsightQuery,
} from 'dashboard/redux/thunk-actions/insight-queries';
import DashboardInsightQueryToolView from 'dashboard/views/DashboardInsightQueryToolView';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useInterval from 'lib/hooks/useInterval';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type DashboardInsightQueryToolRouteProps = PropsFromRedux;

const DashboardInsightQueryToolRoute = (props: DashboardInsightQueryToolRouteProps) => {
  const location = useLocation();
  const [code, setCode] = useState('');
  const [queryName, setQueryName] = useState<string>('');
  const [isPollingForTables, setIsPollingForTables] = useState(false);
  const isExportsEnabled = useFeatureFlag('dashboard:enable-insight-queries-exports');
  const isSensitiveDataModeEnabled = useFeatureFlag(
    'dashboard:enable-insight-queries-sensitive-queries',
  );

  const token = location.pathname.split('/').pop();
  useEffect(() => {
    if (token?.startsWith('isq_')) {
      props.setActiveQueryByToken({ token });
    }
  }, [token, props, props.setActiveQueryByToken]);

  useEffect(() => {
    if (props.activeQuery) {
      setQueryName(props.activeQuery.attributes.name);
      setCode(props.activeQuery.attributes.query);
    }
  }, [props.activeQuery]);

  const handleCodeChange = (value: string) => {
    setCode(value);
  };

  const {
    callback: handleInsightQueriesExportCreate,
    loading: isLoadingInsightQueriesExport,
    error: createInsightQueriesExportError,
  } = useApiCallback(() => props.createExporterInsightQuery({ query: code }), [code]);
  useApiErrorHandler({
    error: createInsightQueriesExportError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to create insight query export, please try again',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Failed to create insight query export, please try again',
        MessageType.Failure,
      ),
  });

  const handleInsightQueriesCreate = async () => {
    await handleInsightQueriesExportCreate();
    props.showMessage('You will receive an email when your export is ready', MessageType.Notice);
  };

  const isForbiddenQueryError = (error: ApiResponseError | undefined) =>
    error?.status === 403 && error?.errors?.[0]?.details === 'query not allowed';

  const {
    callback: handleInsightQueriesResultsPreview,
    loading: isLoadingInsightQueriesResultsPreview,
    error: loadInsightQueriesResultsPreviewError,
  } = useApiCallback(async () => {
    if (!code) {
      props.showMessage('Query can not be blank, please try again');
      props.clearInsightQueriesResults();
      return;
    }
    await props.getInsightQueriesPreview({ query: code });
  }, [code]);
  useApiErrorHandler({
    error: loadInsightQueriesResultsPreviewError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (isForbiddenQueryError(error)) {
        props.showMessage(
          'Your query includes an unsupported operation. Please try again',
          MessageType.Failure,
        );
      } else {
        props.showMessage('Failed to load results, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to load results, please try again', MessageType.Failure),
  });

  const {
    callback: handleInsightQueryCreate,
    loading: isLoadingInsightQueryCreate,
    error: createInsightQueryError,
  } = useApiCallback(async () => {
    await props.createInsightQuery({ name: queryName, query: code });
  }, [queryName, code]);

  useApiErrorHandler({
    error: createInsightQueryError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (isForbiddenQueryError(error)) {
        props.showMessage(
          'Your query includes an unsupported operation. Please try again',
          MessageType.Failure,
        );
      } else {
        props.showMessage('Failed to save query, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to save query, please try again', MessageType.Failure),
  });

  const {
    callback: handleInsightQueryUpdate,
    loading: isLoadingInsightQueryUpdate,
    error: updateInsightQueryError,
  } = useApiCallback(async () => {
    if (props.activeQuery != null) {
      await props.updateInsightQuery({ id: props.activeQuery.id, name: queryName, query: code });
    }
  }, [props, props.activeQuery, queryName, code]);

  useApiErrorHandler({
    error: updateInsightQueryError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (isForbiddenQueryError(error)) {
        props.showMessage(
          'Your query includes an unsupported operation. Please try again',
          MessageType.Failure,
        );
      } else {
        props.showMessage('Failed to update query, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to update query, please try again', MessageType.Failure),
  });

  const {
    callback: handleExpandData,
    loading: isLoadingExpandData,
    error: expandDataError,
  } = useApiCallback(async () => {
    await props.expandData();
    setIsPollingForTables(true);
  }, [props, props.expandData]);

  useApiErrorHandler({
    error: expandDataError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load data, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to load data, please try again', MessageType.Failure),
  });

  const { callback: checkExpandedTableExists, error: checkExpandedTableError } =
    useApiCallback(async () => {
      await props.checkExpandedTableExists();
      if (props.expandedTablesAvailable) {
        setIsPollingForTables(false);
        props.showMessage('Sensitive data tables are ready', MessageType.Success);
      }
    }, [props.checkExpandedTableExists, props.expandedTablesAvailable]);

  useApiErrorHandler({
    error: checkExpandedTableError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to check table status', MessageType.Failure);
      }
      setIsPollingForTables(false);
    },
    onNetworkError: () => {
      props.showMessage('Failed to check table status', MessageType.Failure);
      setIsPollingForTables(false);
    },
  });

  useInterval({
    callback: checkExpandedTableExists,
    // @ts-expect-error [strict-migration]: Remove once useInterval prop types are updated
    delay: isPollingForTables ? 25000 : null,
    pauseWhenBackgrounded: true,
  });

  const handleOnSaveClick = () => {
    if (props.activeQuery != null) {
      handleInsightQueryUpdate();
    } else {
      handleInsightQueryCreate();
    }
  };
  const convertResultsToCSV = (
    results: InsightQueriesPreviewResults,
    headers: string[],
  ): string => {
    const csvRows = results.map((row) => headers.map((header) => row[header] ?? '').join(','));
    return [headers.join(','), ...csvRows].join('\n');
  };

  const handleDownloadCsv = () => {
    if (props.previewResults.length === 0) {
      return;
    }
    const columnNameHeaders = Object.keys(props.previewResults[0]);
    const csvContent = convertResultsToCSV(props.previewResults, columnNameHeaders);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'persona-insight-query-results.csv');
  };

  const showTableLoader = isLoadingInsightQueriesExport || isLoadingInsightQueriesResultsPreview;

  const isLoading =
    isLoadingInsightQueryCreate ||
    isLoadingInsightQueryUpdate ||
    isLoadingInsightQueriesExport ||
    isLoadingInsightQueriesResultsPreview;

  return (
    <DashboardInsightQueryToolView
      code={code}
      handleChangeQueryName={setQueryName}
      handleDownloadCsv={handleDownloadCsv}
      loading={isLoading}
      showTableLoader={showTableLoader}
      expandData={handleExpandData}
      expandedTablesAvailable={props.expandedTablesAvailable}
      isLoadingExpandData={isLoadingExpandData}
      isExportsEnabled={isExportsEnabled}
      isSensitiveDataModeEnabled={isSensitiveDataModeEnabled}
      onCodeChange={handleCodeChange}
      onExport={handleInsightQueriesCreate}
      onPreview={handleInsightQueriesResultsPreview}
      onSave={handleOnSaveClick}
      previewResults={props.previewResults}
      sqlError={props.previewError}
      queryName={queryName}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  activeQuery: state.insightQueries.activeQuery,
  previewResults: state.insightQueries.results,
  previewError: state.insightQueries.error,
  expandedTablesAvailable: state.insightQueries.expandedTablesAvailable,
});

const mapDispatchToProps = {
  createExporterInsightQuery,
  createInsightQuery,
  clearInsightQueriesResults,
  getInsightQueriesPreview,
  setActiveQueryByToken,
  showMessage,
  updateInsightQuery,
  expandData,
  checkExpandedTableExists,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardInsightQueryToolRoute);
